import Image from "next/image";

interface SocialProofMetric {
  value: string;
  label: string;
}

interface SocialProofItem {
  id: string;
  industry?: string;
  companySize?: string;
  quote: string;
  attribution?: string;
  context?: string;
  metrics?: SocialProofMetric[];
  tags?: string[];
  /** Customer logo — only a named reference has one, which is what separates
   *  it from the anonymised examples sitting in the same list. */
  logo?: string;
  logoAlt?: string;
}

interface SocialProofQuoteProps {
  items: SocialProofItem[];
  trustNote?: string;
  subtitle?: string;
  /** "rows" (default) stacks full-width quotes; "columns" spreads short
   *  testimonials side by side across the full content width. */
  layout?: "rows" | "columns";
}

/**
 * SocialProofQuote
 *
 * Anonymised case-quote section used by `socialProof.items`-style data.
 * Each item is a row with an industry tag, the quote itself, attribution,
 * an optional context paragraph, topic chips and a metric strip (savings,
 * runtime, ...). An item with a `logo` is a named reference and gets the
 * customer mark plus a heavier orange top rule, which is what distinguishes it
 * from the anonymised examples listed alongside it.
 * Borderless by design — the hairline and the orange metric values carry the
 * structure, so quotes never turn into tinted, colour-coded cards.
 */
export default function SocialProofQuote({
  items,
  trustNote,
  subtitle,
  layout = "rows",
}: SocialProofQuoteProps) {
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error("SocialProofQuote: `items` array is empty or invalid.");
  }

  return (
    <div className="social-proof">
      {subtitle ? <p className="social-proof-subtitle">{subtitle}</p> : null}
      <div
        className={
          layout === "columns"
            ? "social-proof-grid social-proof-grid-columns"
            : "social-proof-grid"
        }
      >
        {items.map((item) => (
          <article
            className={
              item.logo
                ? "social-proof-card social-proof-card-named"
                : "social-proof-card"
            }
            key={item.id}
          >
            {item.industry || item.logo ? (
              <div className="social-proof-head">
                {item.logo ? (
                  <span className="social-proof-logo">
                    <Image
                      src={item.logo}
                      alt={item.logoAlt ?? ""}
                      width={44}
                      height={44}
                    />
                  </span>
                ) : null}
                {item.industry ? (
                  <div className="social-proof-tag">
                    <span>{item.industry}</span>
                    {item.companySize ? <span>· {item.companySize}</span> : null}
                  </div>
                ) : null}
              </div>
            ) : null}
            <blockquote className="social-proof-quote-text">
              <p>&ldquo;{item.quote}&rdquo;</p>
              {item.attribution ? (
                <footer>
                  <cite>{item.attribution}</cite>
                </footer>
              ) : null}
            </blockquote>
            {item.context ? (
              <p className="social-proof-context">{item.context}</p>
            ) : null}
            {Array.isArray(item.tags) && item.tags.length > 0 ? (
              <ul className="social-proof-tags">
                {item.tags.map((tag) => (
                  <li key={tag}>{tag}</li>
                ))}
              </ul>
            ) : null}
            {Array.isArray(item.metrics) && item.metrics.length > 0 ? (
              <ul className="social-proof-metrics">
                {item.metrics.map((metric) => (
                  <li key={`${metric.value}-${metric.label}`}>
                    <strong>{metric.value}</strong>
                    <span>{metric.label}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </article>
        ))}
      </div>
      {trustNote ? <p className="social-proof-trust">{trustNote}</p> : null}
    </div>
  );
}
